import React, { useContext, useMemo, useState } from 'react'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'
import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Collapse from '@mui/material/Collapse'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import AddIcon from '@mui/icons-material/Add'
import CampaignIcon from '@mui/icons-material/Campaign'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import DynamicFeedIcon from '@mui/icons-material/DynamicFeed'
import EmailIcon from '@mui/icons-material/Email'
import EmojiPeopleIcon from '@mui/icons-material/EmojiPeople'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import MoveToInboxIcon from '@mui/icons-material/MoveToInbox'
import OutboxIcon from '@mui/icons-material/Outbox'
import PersonOffIcon from '@mui/icons-material/PersonOff'
import SmsIcon from '@mui/icons-material/Sms'
import StickyNote2Icon from '@mui/icons-material/StickyNote2'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import EventBusyIcon from '@mui/icons-material/EventBusy'
import BigModal from '../../../components/Modals/BigModal'
import SmallModal from '../../../components/Modals/SmallModal'
import CustomDataGrid from '../../../components/DataGrid/CustomDataGrid'
import API from '../../../services/API'
import { GlobalContext, TenantSettingsContext } from '../../../App'
import OutboundSMS from '../../system/OutboundSMS'
import AddNewShift from './AddNewShift'
import RejectedShiftApplications from './Shifts/RejectedShiftApplications'
import MassUpdateShifts from './Shifts/MassUpdateShifts'
import EventShiftApplications, {
  useEventShiftApplications,
} from './Shifts/EventShiftApplications'
import ApplicationReviewDrawer from '../../admin/ShiftApplications/ApplicationReviewDrawer'
import { EventMetaContext, ShiftContext } from './SingleEvent'

const shiftIsCovered = (shift) =>
  !!shift?.assignedTo ||
  !!shift?.assignedSubcontractor ||
  !!shift?.assignedToSubcontractor

const getAssignedShiftLabel = (shift) => {
  if (shift?.assignedTo) {
    return `${shift.assignedTo.userAccount?.firstName ?? ''} ${
      shift.assignedTo.userAccount?.lastName ?? ''
    }`.trim()
  }

  if (shift?.assignedSubcontractor) {
    return `${shift.assignedSubcontractor.userAccount?.firstName ?? ''} ${
      shift.assignedSubcontractor.userAccount?.lastName ?? ''
    }`.trim()
  }

  if (shift?.assignedToSubcontractor) return 'Subcontractor'

  return 'Unassigned'
}

const getAssignedShiftPhotograph = (shift) =>
  shift?.assignedTo?.userAccount?.photograph ||
  shift?.assignedSubcontractor?.userAccount?.photograph

const getAssignedShiftInitials = (shift) => {
  const label = getAssignedShiftLabel(shift)
  return label
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
}

const formatHours = (shift) => {
  const hours = moment(shift.end).diff(moment(shift.start), 'hours', true)
  if (isNaN(hours)) return 0
  return Number.isInteger(hours) ? hours : parseFloat(hours.toFixed(2))
}

// The assignee is still on the shift but has offered it for a swap or said
// they cannot attend - either way it needs covering.
const CoverFlag = ({ shift }) => {
  if (moment(shift.end).isBefore(moment())) return null
  if (shift.acknowledgement?.declinedAt) {
    return (
      <Tooltip
        title={`Cannot attend${
          shift.acknowledgement.declineReason
            ? `: ${shift.acknowledgement.declineReason}`
            : ''
        }${shift.availableForReassignment ? ' - offered to colleagues' : ''}`}
      >
        <EventBusyIcon color="error" fontSize="small" />
      </Tooltip>
    )
  }
  if (shift.availableForReassignment) {
    return (
      <Tooltip title="Offered for swap - still assigned until reassigned">
        <SwapHorizIcon color="anPurple" fontSize="small" />
      </Tooltip>
    )
  }
  return null
}

const shiftColumns = (onOpenApplications) => [
  {
    field: 'time',
    headerName: 'Time',
    valueGetter: (value, row) => row.start,
    renderCell: ({ row }) =>
      `${moment(row.start).format('HH:mm')} – ${moment(row.end).format('HH:mm')}`,
    minWidth: 110,
  },
  {
    field: 'hours',
    headerName: 'Hours',
    valueGetter: (value, row) => formatHours(row),
    minWidth: 70,
  },
  {
    field: 'clinicalRole',
    headerName: 'Role',
    minWidth: 110,
    flex: 1,
  },
  {
    field: 'assignedTo',
    headerName: 'Staff',
    flex: 1.2,
    minWidth: 180,
    valueGetter: (value) => value?._id,
    renderCell: ({ row }) => (
      <Box height="100%" width="100%" display="flex" alignItems="center">
        {shiftIsCovered(row) ? (
          <Tooltip title={getAssignedShiftLabel(row)}>
            <Stack direction="row" alignItems="center" gap={1} minWidth={0}>
              <Avatar
                src={getAssignedShiftPhotograph(row)}
                sx={{ borderRadius: 1, width: 24, height: 24, fontSize: 12 }}
              >
                {getAssignedShiftInitials(row)}
              </Avatar>
              <Typography variant="body2" noWrap>
                {getAssignedShiftLabel(row)}
              </Typography>
              <CoverFlag shift={row} />
            </Stack>
          </Tooltip>
        ) : (
          <Chip
            size="small"
            label="Unassigned"
            color="error"
            variant="outlined"
            sx={{ borderRadius: 1, height: 22, fontSize: '0.7rem', fontWeight: 700 }}
          />
        )}
      </Box>
    ),
  },
  {
    field: 'eventAssignment',
    headerName: 'Assignment',
    minWidth: 140,
    flex: 1,
  },
  {
    field: 'staffCanBook',
    headerName: 'Bookable',
    align: 'center',
    headerAlign: 'center',
    minWidth: 80,
    renderCell: ({ row }) =>
      row.staffCanBook && (
        <Box
          height="100%"
          width="100%"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Tooltip title="Staff can book this shift">
            <CheckCircleIcon color="success" fontSize="small" />
          </Tooltip>
        </Box>
      ),
  },
  {
    field: 'flags',
    headerName: '',
    minWidth: 64,
    width: 64,
    renderCell: ({ row }) => (
      <Box
        height="100%"
        width="100%"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Stack direction="row" alignItems="center">
          {row.shiftNote && (
            <Tooltip title={row.shiftNote}>
              <StickyNote2Icon color="warning" fontSize="small" />
            </Tooltip>
          )}
          {row.applications?.some((x) => x.status === 'Pending') && (
            <Tooltip title="Pending applications">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation()
                  onOpenApplications(row)
                }}
              >
                <EmojiPeopleIcon color="anPurple" fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Box>
    ),
  },
]

const Shifts = ({ expandedDays, setExpandedDays }) => {
  const { loadEvent, eventMeta } = useContext(EventMetaContext)
  const { shifts } = useContext(ShiftContext)
  const { setToast } = useContext(GlobalContext)
  const navigate = useNavigate()

  const [addNew, setAddNew] = useState(false)
  const [existingShift, setExistingShift] = useState({})
  const [expanded, setExpanded] = useState(true)
  const [menuAnchorEl, setMenuAnchorEl] = useState(null)
  const [openSendSMS, setOpenSendSMS] = useState(false)
  const [openMassUpdate, setOpenMassUpdate] = useState(false)
  const [openAvailableShiftEmail, setOpenAvailableShiftEmail] = useState(false)
  const [openRejectedApplications, setOpenRejectedApplications] =
    useState(false)
  const [loadingAdhocEmail, setLoadingAdhocEmail] = useState(false)
  const [selectedApplicationGroup, setSelectedApplicationGroup] =
    useState(null)

  // Changes whenever the event's shifts show a different set of pending
  // applications, so the applications panel refetches.
  const pendingApplicationsKey = useMemo(
    () =>
      shifts
        .flatMap((shift) =>
          (shift.applications ?? [])
            .filter((application) => application.status === 'Pending')
            .map((application) => application._id),
        )
        .sort()
        .join(','),
    [shifts],
  )
  const {
    groups: applicationGroups,
    groupByShiftID,
    loading: loadingApplications,
    error: applicationsError,
    reload: reloadApplications,
  } = useEventShiftApplications(eventMeta._id, pendingApplicationsKey)

  // A shift's applications icon opens its review row here; if the row isn't
  // loaded yet, fall back to the Shift Applications page.
  const openShiftApplications = (shift) => {
    const group = groupByShiftID.get(String(shift._id))
    if (group) setSelectedApplicationGroup(group)
    else navigate('/shift-applications')
  }

  const daysAr = shifts.reduce((accumulator, shift) => {
    const dayStr = moment(shift.start).startOf('day').toISOString()
    if (!accumulator[dayStr]) accumulator[dayStr] = []
    accumulator[dayStr].push(shift)
    return accumulator
  }, {})
  const days = Object.keys(daysAr).sort((a, b) =>
    moment(a) < moment(b) ? -1 : 1,
  )

  const totalShifts = shifts.length
  const filledShifts = shifts.filter(shiftIsCovered).length

  const allStaffOnShift = [
    ...new Set(
      shifts
        .map((shift) => shift.assignedTo?.userAccount?._id)
        .filter(Boolean),
    ),
  ]
  const allStaffOnShiftEmails = [
    ...new Set(
      shifts
        .map((shift) => shift.assignedTo?.userAccount?.email)
        .filter(Boolean),
    ),
  ]
  const mailto = `mailto:?bcc=${allStaffOnShiftEmails
    .map((email) => encodeURIComponent(email.trim()))
    .join(';')}&subject=${encodeURIComponent(eventMeta.title ?? '')}`

  // Organisation-level switch (Configuration > Shifts). Missing = enabled.
  const manualShiftNotificationsEnabled =
    useContext(TenantSettingsContext).orgConfig?.orgSettings
      ?.shiftAvailabilityNotifications?.manual !== false

  const closeMenu = () => setMenuAnchorEl(null)

  const sendAvailableShiftEmailToAllStaff = () => {
    setLoadingAdhocEmail(true)
    API.post('/shifts/availableshiftsad-hoc', {
      event: eventMeta._id,
    })
      .then((res) => {
        setLoadingAdhocEmail(false)
        setOpenAvailableShiftEmail(false)
        if (res.status === 200) {
          const count = res.data?.notified
          setToast({
            message:
              typeof count === 'number'
                ? `Notification sent to ${count} staff member${count === 1 ? '' : 's'}`
                : 'Notification sent',
          })
        } else {
          setToast({
            severity: 'error',
            message:
              res.data?.message ||
              'Unable to send the notification. Please try again later.',
          })
        }
      })
      .catch((e) => {
        setLoadingAdhocEmail(false)
        setOpenAvailableShiftEmail(false)
        setToast({
          severity: 'error',
          message: 'Unable to send email. Please try again later.',
        })
        console.error(e)
      })
  }

  const handleBulkBookable = (bookable) => {
    closeMenu()
    API.post('/events/shifts/bulkbookable', {
      eventID: eventMeta._id,
      bookable,
    }).then((res) => res.status === 200 && loadEvent())
  }

  const handleEmailStaff = () => {
    closeMenu()
    window.location.href = mailto
    navigator.clipboard.writeText(allStaffOnShiftEmails.join(','))
    setToast({ message: 'Emails copied to clipboard' })
  }

  const noAssignedStaff = allStaffOnShift.length === 0

  return (
    <Box
      component={Paper}
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 3,
        border: '1px solid rgba(15, 23, 42, 0.08)',
        boxShadow: '0 8px 24px rgba(15, 23, 42, 0.05)',
        bgcolor: 'background.paper',
      }}
    >
      <Stack gap={1.5}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap={1}
          flexWrap="wrap"
        >
          <Stack gap={0.25}>
            <Stack direction="row" alignItems="center" gap={1}>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                Shifts
              </Typography>
              {totalShifts > 0 && (
                <Chip
                  size="small"
                  label={`${filledShifts} / ${totalShifts} filled`}
                  color={filledShifts === totalShifts ? 'success' : 'error'}
                  variant={filledShifts === totalShifts ? 'filled' : 'outlined'}
                  sx={{
                    borderRadius: 1,
                    height: 20,
                    fontSize: '0.68rem',
                    fontWeight: 700,
                  }}
                />
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {totalShifts === 0
                ? 'No shifts have been added yet'
                : `${totalShifts} shift${totalShifts === 1 ? '' : 's'} across ${
                    days.length
                  } day${days.length === 1 ? '' : 's'}`}
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" gap={0.5}>
            <Button
              size="small"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setExistingShift({})
                setAddNew(true)
              }}
            >
              Add shift
            </Button>
            <Tooltip title="More actions">
              <IconButton
                size="small"
                onClick={(e) => setMenuAnchorEl(e.currentTarget)}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={expanded ? 'Collapse' : 'Expand'}>
              <IconButton size="small" onClick={() => setExpanded(!expanded)}>
                <ExpandMoreIcon
                  fontSize="small"
                  sx={{
                    transform: expanded ? 'rotate(180deg)' : 'none',
                    transition: 'transform 0.2s ease',
                  }}
                />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        <Menu open={!!menuAnchorEl} onClose={closeMenu} anchorEl={menuAnchorEl}>
          <MenuItem
            onClick={() => {
              closeMenu()
              setOpenMassUpdate(true)
            }}
            disabled={totalShifts === 0}
          >
            <ListItemIcon>
              <DynamicFeedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Mass update"
              secondary="Change times, role, pay and more on many shifts"
            />
          </MenuItem>
          <Divider />
          <MenuItem
            onClick={() => handleBulkBookable(true)}
            disabled={totalShifts === 0}
          >
            <ListItemIcon>
              <OutboxIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Make all shifts bookable</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => handleBulkBookable(false)}
            disabled={totalShifts === 0}
          >
            <ListItemIcon>
              <MoveToInboxIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Make all shifts unbookable</ListItemText>
          </MenuItem>
          <Divider />
          <MenuItem
            onClick={() => {
              closeMenu()
              setOpenSendSMS(true)
            }}
            disabled={noAssignedStaff}
          >
            <ListItemIcon>
              <SmsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="SMS assigned staff"
              secondary="Recorded as an internal note"
            />
          </MenuItem>
          <MenuItem onClick={handleEmailStaff} disabled={noAssignedStaff}>
            <ListItemIcon>
              <EmailIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Email assigned staff"
              secondary="Opens your mail app with everyone BCC'd"
            />
          </MenuItem>
          {manualShiftNotificationsEnabled && (
            <MenuItem
              onClick={() => {
                closeMenu()
                setOpenAvailableShiftEmail(true)
              }}
            >
              <ListItemIcon>
                <CampaignIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Notify eligible staff"
                secondary="Advertise the available shifts"
              />
            </MenuItem>
          )}
          <Divider />
          <MenuItem
            onClick={() => {
              closeMenu()
              setOpenRejectedApplications(true)
            }}
          >
            <ListItemIcon>
              <PersonOffIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Rejected shift applications</ListItemText>
          </MenuItem>
        </Menu>

        <Collapse in={expanded}>
          <Stack gap={1.5}>
            <EventShiftApplications
              groups={applicationGroups}
              loading={loadingApplications}
              error={applicationsError}
              onOpen={setSelectedApplicationGroup}
            />
            {days.length === 0 && (
              <Stack
                alignItems="center"
                gap={1}
                sx={{
                  py: 3,
                  px: 2,
                  borderRadius: 2,
                  border: '1px dashed rgba(15, 23, 42, 0.18)',
                  textAlign: 'center',
                }}
              >
                <Typography variant="body2" fontWeight={700}>
                  No shifts yet
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Add the duties this event needs, then assign staff or open
                  them for booking.
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => setAddNew(true)}
                  sx={{ mt: 0.5 }}
                >
                  Add shift
                </Button>
              </Stack>
            )}
            {days.map((day) => {
              const dayShifts = daysAr[day]
              const dayTotal = dayShifts.length
              const dayFilled = dayShifts.filter(shiftIsCovered).length
              const isGood = dayTotal === dayFilled
              const normalizedDay = moment(day).startOf('day').toISOString()
              const isExpanded = expandedDays.includes(normalizedDay)
              return (
                <Accordion
                  key={day}
                  disableGutters
                  elevation={0}
                  expanded={isExpanded}
                  onChange={() =>
                    isExpanded
                      ? setExpandedDays((prev) =>
                          prev.filter((x) => x !== normalizedDay),
                        )
                      : setExpandedDays((prev) => [...prev, normalizedDay])
                  }
                  sx={{
                    border: '1px solid rgba(15, 23, 42, 0.1)',
                    borderRadius: '10px !important',
                    overflow: 'hidden',
                    '&:before': { display: 'none' },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      minHeight: 52,
                      bgcolor: isExpanded ? '#fbfcfe' : 'background.paper',
                      borderLeft: '4px solid',
                      borderLeftColor: isGood ? 'success.main' : 'error.main',
                      '& .MuiAccordionSummary-content': {
                        alignItems: 'center',
                        gap: 1.5,
                        my: 1,
                        minWidth: 0,
                      },
                    }}
                  >
                    <Typography fontWeight={700} noWrap>
                      {moment(day).format('ddd Do MMM YYYY')}
                    </Typography>
                    <Chip
                      size="small"
                      label={`${dayFilled} / ${dayTotal} filled`}
                      color={isGood ? 'success' : 'error'}
                      variant={isGood ? 'filled' : 'outlined'}
                      sx={{
                        borderRadius: 1,
                        height: 20,
                        fontSize: '0.68rem',
                        fontWeight: 700,
                      }}
                    />
                  </AccordionSummary>
                  <AccordionDetails
                    sx={{
                      p: 0,
                      overflowX: 'auto',
                      borderTop: '1px solid rgba(15, 23, 42, 0.08)',
                    }}
                  >
                    <CustomDataGrid
                      slots={{ footer: () => null, toolbar: () => null }}
                      rows={[...dayShifts].sort((a, b) => {
                        const startDiff =
                          moment(a.start).valueOf() - moment(b.start).valueOf()
                        if (startDiff !== 0) return startDiff
                        return (a?.eventAssignment ?? '').localeCompare(
                          b?.eventAssignment ?? '',
                        )
                      })}
                      columns={shiftColumns(openShiftApplications)}
                      getRowId={(row) => row._id}
                      disableRowSelectionOnClick
                      onRowClick={({ row }) => {
                        setExistingShift(row)
                        setAddNew(true)
                      }}
                      noMinHeight
                      disableColumnMenu
                      disableColumnFilter
                      disableColumnSelector
                      disableAutosize
                      disableColumnSorting
                      // The accordion already frames the grid, so drop the
                      // grid's own 2px outline
                      sx={{
                        borderRadius: 0,
                        '& .MuiDataGrid-main': { border: 0, borderRadius: 0 },
                      }}
                    />
                  </AccordionDetails>
                </Accordion>
              )
            })}
          </Stack>
        </Collapse>
      </Stack>

      <BigModal open={openSendSMS} onClose={() => setOpenSendSMS(false)}>
        <OutboundSMS
          passedEmployees={allStaffOnShift}
          eventLink={eventMeta._id}
          onClose={() => {
            setToast({ message: 'SMS sent and recorded as an internal note' })
            setOpenSendSMS(false)
            // The server appends the note to plannerComments; refetch so the
            // Internal Notes panel shows it straight away
            loadEvent()
          }}
        />
      </BigModal>
      <SmallModal
        open={openAvailableShiftEmail}
        onClose={() => setOpenAvailableShiftEmail(false)}
      >
        <Stack gap={1} alignItems="center">
          <Typography variant="body2" textAlign="center">
            Notify all staff whose roles match these available shifts, by
            email and/or app push according to their own notification
            preferences.
          </Typography>
          <Typography variant="subtitle2" textAlign="center">
            The automatic daily and weekly digests are unaffected and can be
            switched off in Configuration &gt; Shifts.
          </Typography>
          <Button
            onClick={() => sendAvailableShiftEmailToAllStaff()}
            color="anGreen"
            variant="contained"
            fullWidth
            disabled={loadingAdhocEmail}
          >
            {loadingAdhocEmail ? 'Sending' : 'Send Now'}
          </Button>
          <Button
            onClick={() => setOpenAvailableShiftEmail(false)}
            color="anRed"
            variant="outlined"
            fullWidth
          >
            Cancel
          </Button>
        </Stack>
      </SmallModal>
      <ApplicationReviewDrawer
        group={selectedApplicationGroup}
        onClose={() => setSelectedApplicationGroup(null)}
        onReviewed={() => {
          reloadApplications()
          // Accepting assigns the shift - refresh the grid's staff column
          loadEvent()
        }}
      />
      <RejectedShiftApplications
        open={openRejectedApplications}
        onClose={() => setOpenRejectedApplications(false)}
      />
      <MassUpdateShifts
        open={openMassUpdate}
        onClose={() => setOpenMassUpdate(false)}
        shifts={shifts}
        eventID={eventMeta._id}
        eventTitle={eventMeta.title}
        onUpdated={loadEvent}
      />
      <BigModal
        open={addNew}
        onClose={() => {
          setAddNew(false)
          setExistingShift({})
        }}
        // The shift editor draws its own sticky header/footer, so the
        // modal shell contributes no padding and a fixed comfortable width
        style={{
          padding: 0,
          width: { xs: 'calc(100vw - 24px)', sm: 'min(1040px, 92vw)' },
          borderRadius: 2,
        }}
      >
        <AddNewShift
          existingShift={existingShift}
          setExistingShift={setExistingShift}
          setExpandedDays={setExpandedDays}
          expandedDays={expandedDays}
          setAddNew={setAddNew}
          hasPendingApplication={existingShift?.applications?.some(
            (x) =>
              x.status === 'Pending' &&
              moment(existingShift.start).isSameOrBefore(moment()),
          )}
        />
      </BigModal>
    </Box>
  )
}

export default React.memo(Shifts)
